// University of Colorado Boulder: public college page (#2840), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CU Boulder's Common Data Set 2025-2026 ("2025-2026 Common Data Set.html"),
//    posted by Data & Analytics in a public SharePoint folder; the
//    data.colorado.edu report page that links it is cited. C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table, C8 test
//    policy (the CDS labels it "Entrance Exams Policy for Fall 2027"),
//    C14/C16 dates, C21/C22 plans, G1/G5 2026-2027 costs.
//  - CU Boulder Admissions, First-Year Applicants: Apply for Admission
//    (deadlines, decision dates, required and optional materials, College of
//    Music steps) and Plan Your Application (selection and test policy). The
//    landing page says "The 2027 application is now open!"
//  - College Scorecard, school id 126614 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1 first-year column (CU's four-year tuition guarantee holds
// that rate) with the G5 resident expenses; the items sum to the totals
// below. G4 says tuition and fees vary by program and 75% of full-time
// undergraduates pay more than the G1 figures.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CU Boulder Common Data Set 2025-2026',
        url: 'https://data.colorado.edu/reports/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'CU Boulder Admissions, First-Year Applicants: Apply for Admission',
        url: 'https://www.colorado.edu/admissions/process/first-year/apply',
        read: '2026-10-01',
    },
    plan: {
        label: 'CU Boulder Admissions, First-Year Applicants: Plan Your Application',
        url: 'https://www.colorado.edu/admissions/process/first-year/plan',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?126614-University-of-Colorado-Boulder',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-colorado-boulder',
    kbId: 'university_of_colorado_boulder',
    name: 'University of Colorado Boulder',
    shortName: 'CU Boulder',
    aliases: ['CU Boulder', 'CU'],
    location: 'Boulder, CO',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CU Boulder visual identity); decoration only.
    branding: {
        primary: '#CFB87C',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_colorado_boulder_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 73180,
        admitted: 51716,
        enrolled: 7397,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 18030, admitted: 14650 },
            { group: 'Out-of-state', applied: 54968, admitted: 37065 },
            { group: 'International', applied: 3121, admitted: 2378 },
            { group: 'Unknown', applied: 182, admitted: 1 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CU Boulder\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'CU Boulder has no early decision plan. Its Early Action is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply with the Common Application and are reviewed with your intended major in mind. One college asks for more:',
        rows: [
            { school: 'College of Music', requirement: 'After you submit the admission application, you also complete a College of Music application, send a letter of reference and schedule an audition. You get no admission decision until after you audition.', source: 'apply' },
        ],
        note: 'Unless you apply to the College of Music, CU Boulder does not accept or use portfolios or audio and video submissions.',
        source: 'plan',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'By February 1', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'Decision dates apply to applications submitted and completed by each deadline; College of Music applicants hear only after their audition. The application fee is $65 for domestic applicants and $70 for international applicants, and fee waivers can be requested on the Common Application.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CU Boulder does not require the SAT or ACT for the 2027 first-year application. You say on the Common App whether you plan to submit scores; if you want them considered, report them in your application or have the testing agency send them by your application deadline so your application is not counted late.',
        source: 'plan',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [13974, 45018] },
            { item: 'Required fees', amounts: [1040, 1040] },
            { item: 'Food and housing (on campus)', amounts: [19242, 19242] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [1314, 1314] },
            { item: 'Other expenses', amounts: [1782, 1782] },
        ],
        totals: [38552, 69596],
        note: 'First-year rates for a full-time undergraduate living on campus; CU\'s four-year tuition guarantee holds tuition at the entry rate. Tuition and fees vary by program, and CU says 75% of full-time undergraduates pay more than these figures.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 23708,
            byIncome: [
                { income: '$0 to $30,000', amount: 13403 },
                { income: '$30,001 to $48,000', amount: 13558 },
                { income: '$48,001 to $75,000', amount: 16684 },
                { income: '$75,001 to $110,000', amount: 21719 },
                { income: 'Over $110,000', amount: 34026 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CU Boulder\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
